import { gql } from '@apollo/client';
import { CohortFragment, CohortRefFragment } from '../cohorts';
import {
  SchemaFieldsFragment,
  SubjectFragment,
  UserAliasReplacementFieldsFragment,
} from '../fragments';
import { FilterFragment } from '../filters';
import { MeasureFragment } from '../measures';
import { FunnelStepFieldsFragment } from '../builderFragments';
import { FieldFieldsFragment } from '../fields';
import { FunnelModeSliceFieldsFragment } from '../fragments';
import { LlmChatMessageFieldsFragment } from '../llm/fragments';

const BreakdownCriteriaFieldsFragment = gql`
  fragment BreakdownCriteriaFields on BreakdownCriteria {
    controlGroup
    field {
      ...FieldFields
    }
    variants
  }
  ${FieldFieldsFragment}
`;

const ExperimentCriteriaFieldsFragment = gql`
  fragment ExperimentCriteriaFields on ExperimentCriteria {
    criteriaControlGroup: controlGroup
    criteriaVariants: variants
    experiment
    experimentField {
      ...FieldFields
    }
    variationField {
      ...FieldFields
    }
  }
  ${FieldFieldsFragment}
`;

const SegmentCriteriaFieldsFragment = gql`
  fragment SegmentCriteriaFields on SegmentCriteria {
    segmentControlGroup: controlGroup {
      ...CohortFields
    }
    segmentVariants: variants {
      ...CohortFields
    }
  }
  ${CohortFragment}
`;

const FunnelMetricFieldsFragment = gql`
  fragment FunnelMetricFields on FunnelMetric {
    convertedOnStep
    ttcMetric
    funnel {
      id
      display
      query {
        steps {
          ...FunnelStepFields
        }
        mode {
          key
        }
        modeSlices {
          ...FunnelModeSliceFields
        }
      }
      pref {
        aliasReplacements {
          ...UserAliasReplacementFields
        }
      }
    }
  }
  ${FunnelStepFieldsFragment}
  ${UserAliasReplacementFieldsFragment}
  ${FunnelModeSliceFieldsFragment}
`;

const MeasureMetricFieldsFragment = gql`
  fragment MeasureMetricFields on MeasureMetric {
    measure {
      ...MeasureFields
    }
  }
  ${MeasureFragment}
`;

const QueryMetricFieldsFragment = gql`
  fragment QueryMetricFields on QueryMetric {
    expression {
      id
      display
      pref {
        aliasReplacements {
          ...UserAliasReplacementFields
        }
      }
    }
    measure {
      ...MeasureFields
    }
  }
  ${UserAliasReplacementFieldsFragment}
`;

const RetentionMetricFieldsFragment = gql`
  fragment RetentionMetricFields on RetentionMetric {
    retention {
      id
      display
    }
  }
`;

export const MetricTypeFieldsFragment = gql`
  fragment MetricTypeFields on MetricType {
    key
    name
  }
`;

export const SignificanceModeFieldsFragment = gql`
  fragment SignificanceModeFields on SignificanceMode {
    key
    name
  }
`;

export const SignificanceTestFieldsFragment = gql`
  fragment SignificanceTestFields on SignificanceTest {
    key
    name
  }
`;

export const ExperimentFieldsFragment = gql`
  fragment ExperimentFields on Experiment {
    experimentField {
      ...FieldFields
    }
    variantField {
      ...FieldFields
    }
  }
  ${FieldFieldsFragment}
`;

export const SignificanceQueryFieldsFragment = gql`
  fragment SignificanceQueryFields on SignificanceQuery {
    cohortRefs {
      ...CohortRefFields
    }
    confidenceLevel
    criteria {
      ... on BreakdownCriteria {
        ...BreakdownCriteriaFields
      }
      ... on ExperimentCriteria {
        ...ExperimentCriteriaFields
      }
      ... on SegmentCriteria {
        ...SegmentCriteriaFields
      }
    }
    filter {
      ...FilterFields
    }
    metric {
      ... on FunnelMetric {
        ...FunnelMetricFields
      }
      ... on MeasureMetric {
        ...MeasureMetricFields
      }
      ... on QueryMetric {
        ...QueryMetricFields
      }
      ... on RetentionMetric {
        ...RetentionMetricFields
      }
    }
    metricType {
      ...MetricTypeFields
    }
    significanceMode: mode {
      ...SignificanceModeFields
    }
    samplePercentage
    schema {
      ...SchemaFields
    }
    significanceLevel
    significanceSubject: subject {
      ...SubjectFields
    }
    test {
      ...SignificanceTestFields
    }
    timeUnit
    llmChatMessages {
      ...LlmChatMessageFields
    }
  }
  ${CohortRefFragment}
  ${BreakdownCriteriaFieldsFragment}
  ${ExperimentCriteriaFieldsFragment}
  ${SegmentCriteriaFieldsFragment}
  ${FunnelMetricFieldsFragment}
  ${MeasureMetricFieldsFragment}
  ${QueryMetricFieldsFragment}
  ${RetentionMetricFieldsFragment}
  ${MetricTypeFieldsFragment}
  ${SignificanceModeFieldsFragment}
  ${SignificanceTestFieldsFragment}
  ${SubjectFragment}
  ${SchemaFieldsFragment}
  ${FilterFragment}
  ${MeasureFragment}
  ${LlmChatMessageFieldsFragment}
`;
